import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import Loader from "./components/Loader";
import { PrivateRoute } from "./components/PrivateRoute";
import { PublicRoute } from "./components/PublicRoute";
import { AuthContext } from "./context/AuthContext";
import { LoaderContext } from "./context/LoaderContext";
import { useToken } from "./helper/hooks";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import GenerateQR from "./pages/GenerateQR";
import GenerateShortUrl from "./pages/GenerateShortUrl";
import HandleRedirection from "./pages/HandleRedirection";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import PageNotFound from "./pages/PageNotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Profile from "./pages/Profile";
import QRList from "./pages/QRList";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import UpdateQr from "./pages/UpdateQR";
import URLList from "./pages/URLList";
import { theme } from "./theme";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined
  );
  const [loader, setLoader] = useState({
    show: false,
    message: "",
  });

  const { token, setToken, removeToken, user } = useToken();

  useEffect(() => {
    let redirect_token = new URLSearchParams(window.location.search).get(
      "redirect_token"
    );
    if (redirect_token) {
      setToken(redirect_token);
    }
    if (token && user) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [token]); // eslint-disable-line

  if (isAuthenticated === undefined) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        token,
        isAuthenticated,
        removeToken,
        setIsAuthenticated,
      }}
    >
      <LoaderContext.Provider
        value={{
          loader,
          showLoader: (loader: { show: boolean; message: string }) =>
            setLoader({ show: loader.show, message: loader.message }),
        }}
      >
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          toastStyle={{
            background: theme.backgroundColor.primaryBlack,
            color: theme.textColor.primaryWhite,
            boxShadow: "0px 0px 10px " + theme.textColor.primaryWhite + "44",
          }}
        />
        <BrowserRouter>
          {loader.show && <Loader />}

          <Routes>
            <Route
              path="/"
              element={<Landing isAuthenticated={!!isAuthenticated} />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/login"
              element={
                <PublicRoute isAuthenticated={!!isAuthenticated}>
                  <Login
                    onLoginSuccess={(token: string) => {
                      setToken(token);
                      setIsAuthenticated(true);
                    }}
                  />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute isAuthenticated={!!isAuthenticated}>
                  <Register
                    onRegisterSuccess={(token: string) => {
                      setToken(token);
                      setIsAuthenticated(true);
                    }}
                  />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute isAuthenticated={!!isAuthenticated}>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/password/reset/:token"
              element={
                <PublicRoute isAuthenticated={!!isAuthenticated}>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route path="/generate/qr" element={<GenerateQR />} />
            <Route
              path="/generate/url"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <GenerateShortUrl />
                </PrivateRoute>
              }
            />
            <Route path="/:shortId" element={<HandleRedirection />} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/qr-list"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <QRList />
                </PrivateRoute>
              }
            />
            <Route
              path="/url-list"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <URLList />
                </PrivateRoute>
              }
            />
            <Route
              path="/update-qr/:updateId"
              element={
                <PrivateRoute isAuthenticated={!!isAuthenticated}>
                  <UpdateQr />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </BrowserRouter>
      </LoaderContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
